import { useState, useRef, useEffect, useCallback, ReactNode, FC, KeyboardEventHandler } from 'react'

import { useLocation } from 'react-router-dom'
import { styled } from 'styled-components'

import { R } from 'core/helpers'
import { boxShadow, outline } from 'core/styles'

import FormInput from 'admin/components/FormInput'
import KeyboardNavArea from 'admin/components/KeyboardNavArea'
import SearchResults from 'admin/components/Search/SearchResults'
import SearchResultsMessage from 'admin/components/Search/SearchResultsMessage'
import {
  hasFocusInside,
  focusFirstFocusableItem,
  focusNextFocusableElement,
  getFocusableElements,
} from 'admin/helpers/focus'
import { useAdminCompanyId, useOnChangeEffect } from 'admin/hooks'

import useOnClickOutside from './useOnClickOutside'

export type UseAppSearchResultsType = ({
  query,
  companyId,
}: {
  query?: string
  companyId?: string
}) => [Array<any>, { isFetching: boolean }]

type SearchProps = {
  useAppSearchResults: UseAppSearchResultsType
  searchTooltipContent?: ReactNode
  placeholder?: string
  showPopover?: boolean
  onAppSearchResults?: (results: Array<any>) => void
  inputBackgroundColor?: string
}

const Search: FC<SearchProps> = ({
  useAppSearchResults,
  searchTooltipContent,
  placeholder,
  showPopover = true,
  onAppSearchResults,
  inputBackgroundColor,
}) => {
  const companyId = useAdminCompanyId()
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement | null>(null)
  const contentRef = useRef<HTMLInputElement | null>(null)

  const [isOpen, setIsOpen] = useState(false)

  const isActive = !!query || isOpen

  const handleClear = useCallback(() => {
    setQuery('')
    inputRef?.current?.focus()
  }, [])

  const onDone = useCallback(() => {
    setTimeout(() => {
      setQuery('')
      setIsOpen(false)
    }, 100)
  }, [])

  useOnChangeEffect(
    ([prevIsActive]) => {
      if (isActive && !prevIsActive) {
        focusFirstFocusableItem(inputRef.current)
      }
      if (!isActive && prevIsActive) {
        setQuery('')
      }
    },
    [isActive],
  )

  const handleInputBlur = useCallback(() => {
    setTimeout(() => {
      if (!hasFocusInside(contentRef, inputRef)) onDone()
    }, 0)
  }, [onDone])

  useEffect(() => {
    const onCommandP = (event: KeyboardEvent) => {
      if (event.key === 'p' && event.metaKey) {
        event.preventDefault()
        setIsOpen((isOpen) => !isOpen)
      }
    }
    document.addEventListener('keydown', onCommandP)

    return () => document.removeEventListener('keydown', onCommandP)
  }, [])

  const location = useLocation()

  const key = location.key

  useOnChangeEffect(
    ([prevKey]) => {
      if (isActive && prevKey && key) onDone()
    },
    [key],
  )

  useOnClickOutside(onDone, contentRef, inputRef)

  const [results, { isFetching }] = useAppSearchResults({
    query,
    companyId,
  })

  const totalNum = R.length(results ?? [])

  const onArrowDown: KeyboardEventHandler = (e) => {
    e.preventDefault()
    focusNextFocusableElement(contentRef)
  }

  const onArrowUp: KeyboardEventHandler = (e) => {
    e.preventDefault()
    focusNextFocusableElement(contentRef, { increment: -1 })
  }

  const handleInputEnter = () => {
    const first = R.first(getFocusableElements(contentRef))
    if (first) first.click()
  }

  useEffect(() => {
    if (onAppSearchResults) {
      onAppSearchResults(results)
    }
  }, [results])

  return (
    <AppSearch>
      <FormInput
        // This design with all of the HOCs is overly complex and challenging to type properly
        // I am not going to spend time dealing with it and have chosen to simply @ts-ignore the following ref prop
        // This stuff should be refactored to use react-hook-form and include labels declaratively in form JSX
        backgroundColor={inputBackgroundColor}
        bare
        bottomBorderOnly
        iconColor='#DADADA'
        iconName='search'
        isFocused={isActive}
        onArrowDown={onArrowDown}
        onArrowUp={onArrowUp}
        onBlur={handleInputBlur}
        onChange={setQuery}
        onEnter={handleInputEnter}
        onEscape={onDone}
        // @ts-ignore
        placeholder={placeholder || 'Search (⌘P)'}
        ref={inputRef}
        tooltipContent={searchTooltipContent}
        value={query}
        width='320px'
      />
      {showPopover && isActive && (
        <SearchResultsBox>
          <KeyboardNavArea
            onArrowDown={onArrowDown}
            onArrowUp={onArrowUp}
            onEscape={onDone}
            onTypeCharacter={() => inputRef.current?.focus()}
            ref={contentRef}
          >
            {isFetching ?
              <SearchResultsMessage actionLabel='Clear query' message='Loading…' onAction={handleClear} />
            : totalNum ?
              <SearchResults onClick={onDone} results={results} />
            : <SearchResultsMessage actionLabel='Clear query' message='No Results' onAction={handleClear} />}
          </KeyboardNavArea>
        </SearchResultsBox>
      )}
    </AppSearch>
  )
}

export default Search

const AppSearch = styled.div`
  display: inline-block;
  position: relative;
`
const SearchResultsBox = styled.div`
  position: absolute;
  right: 22px;
  bottom: 0;
  left: 24px;
  transform: translateY(calc(100% - 2px));
  z-index: 1;
  border: 1px solid ${outline};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  box-shadow: ${boxShadow};
  background-color: white;
  padding: 8px 4px;
  max-height: inherit;
  overflow-y: auto;
`
